/**
 * Socket.io client service
 * Manages real-time connections and event handling
 */

import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '@/utils/constants';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  SocketConnectionState,
  JobProgressEvent,
  JobStatusEvent,
  JobCompleteEvent,
  JobErrorEvent,
  CellUpdateEvent,
  StatsUpdateEvent,
  LogEntryEvent,
} from '@/types/socket';

/**
 * Typed socket instance
 */
type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Socket singleton instance
 */
let socket: TypedSocket | null = null;

/**
 * Connection state listeners
 */
type ConnectionStateListener = (state: SocketConnectionState) => void;
const connectionStateListeners: Set<ConnectionStateListener> = new Set();

/**
 * Current connection state
 */
let currentConnectionState: SocketConnectionState = 'disconnected';

/**
 * Get or create socket instance
 */
export function getSocket(): TypedSocket {
  if (!socket) {
    socket = io(SOCKET_URL || window.location.origin, {
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling'],
    });

    // Set up connection state handlers
    socket.on('connect', () => {
      updateConnectionState('connected');
    });

    socket.on('disconnect', () => {
      updateConnectionState('disconnected');
    });

    socket.io.on('reconnect_attempt', () => {
      updateConnectionState('reconnecting');
    });

    socket.io.on('reconnect_failed', () => {
      updateConnectionState('disconnected');
    });
  }

  return socket;
}

/**
 * Update connection state and notify listeners
 */
function updateConnectionState(state: SocketConnectionState): void {
  currentConnectionState = state;
  connectionStateListeners.forEach((listener) => listener(state));
}

/**
 * Connect to socket server
 */
export function connect(): void {
  const s = getSocket();
  if (!s.connected) {
    updateConnectionState('connecting');
    s.connect();
  }
}

/**
 * Disconnect from socket server
 */
export function disconnect(): void {
  if (socket?.connected) {
    socket.disconnect();
  }
}

/**
 * Check if socket is connected
 */
export function isConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Get current connection state
 */
export function getConnectionState(): SocketConnectionState {
  return currentConnectionState;
}

/**
 * Subscribe to connection state changes
 */
export function onConnectionStateChange(
  listener: ConnectionStateListener
): () => void {
  connectionStateListeners.add(listener);
  return () => connectionStateListeners.delete(listener);
}

// ============ Job Subscriptions ============

/**
 * Subscribe to a specific job's updates
 */
export function subscribeToJob(jobId: string): void {
  const s = getSocket();
  if (s.connected) {
    s.emit('subscribe:job', jobId);
  }
}

/**
 * Unsubscribe from a job's updates
 */
export function unsubscribeFromJob(jobId: string): void {
  const s = getSocket();
  if (s.connected) {
    s.emit('unsubscribe:job', jobId);
  }
}

/**
 * Subscribe to dashboard updates
 */
export function subscribeToDashboard(): void {
  const s = getSocket();
  if (s.connected) {
    s.emit('subscribe:dashboard');
  }
}

/**
 * Unsubscribe from dashboard updates
 */
export function unsubscribeFromDashboard(): void {
  const s = getSocket();
  if (s.connected) {
    s.emit('unsubscribe:dashboard');
  }
}

// ============ Event Listeners ============

/**
 * Listen for job progress updates
 */
export function onJobProgress(
  jobId: string,
  callback: (data: JobProgressEvent) => void
): () => void {
  const s = getSocket();
  const handler = (data: JobProgressEvent) => {
    if (data.jobId === jobId) {
      callback(data);
    }
  };
  s.on('job:progress', handler);
  return () => s.off('job:progress', handler);
}

/**
 * Listen for job status changes
 */
export function onJobStatus(
  jobId: string,
  callback: (data: JobStatusEvent) => void
): () => void {
  const s = getSocket();
  const handler = (data: JobStatusEvent) => {
    if (data.jobId === jobId) {
      callback(data);
    }
  };
  s.on('job:status', handler);
  return () => s.off('job:status', handler);
}

/**
 * Listen for job completion
 */
export function onJobComplete(
  jobId: string,
  callback: (data: JobCompleteEvent) => void
): () => void {
  const s = getSocket();
  const handler = (data: JobCompleteEvent) => {
    if (data.jobId === jobId) {
      callback(data);
    }
  };
  s.on('job:complete', handler);
  return () => s.off('job:complete', handler);
}

/**
 * Listen for job errors
 */
export function onJobError(
  jobId: string,
  callback: (data: JobErrorEvent) => void
): () => void {
  const s = getSocket();
  const handler = (data: JobErrorEvent) => {
    if (data.jobId === jobId) {
      callback(data);
    }
  };
  s.on('job:error', handler);
  return () => s.off('job:error', handler);
}

/**
 * Listen for cell updates
 */
export function onCellUpdate(
  jobId: string,
  callback: (data: CellUpdateEvent) => void
): () => void {
  const s = getSocket();
  const handler = (data: CellUpdateEvent) => {
    if (data.jobId === jobId) {
      callback(data);
    }
  };
  s.on('cell:update', handler);
  return () => s.off('cell:update', handler);
}

/**
 * Listen for dashboard stats updates
 */
export function onStatsUpdate(
  callback: (data: StatsUpdateEvent) => void
): () => void {
  const s = getSocket();
  s.on('stats:update', callback);
  return () => s.off('stats:update', callback);
}

/**
 * Listen for log entries
 */
export function onLogEntry(
  jobId: string,
  callback: (data: LogEntryEvent) => void
): () => void {
  const s = getSocket();
  const handler = (data: LogEntryEvent) => {
    if (data.jobId === jobId) {
      callback(data);
    }
  };
  s.on('log:entry', handler);
  return () => s.off('log:entry', handler);
}

// ============ Utility Functions ============

/**
 * Wait for socket to connect
 */
export function waitForConnection(timeoutMs: number = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isConnected()) {
      resolve();
      return;
    }

    const s = getSocket();
    const timeout = setTimeout(() => {
      reject(new Error('Socket connection timeout'));
    }, timeoutMs);

    const onConnect = () => {
      clearTimeout(timeout);
      s.off('connect', onConnect);
      resolve();
    };

    s.on('connect', onConnect);
    connect();
  });
}

/**
 * Reconnect if disconnected
 */
export function ensureConnected(): void {
  if (!isConnected()) {
    connect();
  }
}

/**
 * Get socket instance (for direct access if needed)
 */
export { socket };

/**
 * Default export for convenience
 */
export default {
  getSocket,
  connect,
  disconnect,
  isConnected,
  getConnectionState,
  onConnectionStateChange,
  subscribeToJob,
  unsubscribeFromJob,
  subscribeToDashboard,
  unsubscribeFromDashboard,
  onJobProgress,
  onJobStatus,
  onJobComplete,
  onJobError,
  onCellUpdate,
  onStatsUpdate,
  onLogEntry,
  waitForConnection,
  ensureConnected,
};
